// Mock data for RABill modules

export interface RABill {
  id: string;
  raBillNumber: string;
  workOrderId: string;
  workOrderNumber: string;
  contractor: string;
  contractorId: string;
  status: 'Draft' | 'Submitted' | 'Checked' | 'Verified' | 'Approved' | 'Posted' | 'Paid' | 'Returned';
  stage: 'Draft' | 'Submitted' | 'Checked' | 'Verified' | 'Approved' | 'Posted' | 'Paid';
  raPeriod: {
    from: string;
    to: string;
  };
  site: string;
  project: string;
  section: string;
  placeOfSupply: string;
  contractorGSTIN: string;
  companyGSTIN: string;
  taxRegime: 'CGST/SGST' | 'IGST';
  tdsSection: string;
  tdsRate: number;
  
  // Commercial Summary
  woTotalValue: number;
  cumulativeWorkDone: number; // Percentage
  cumulativeWorkValue: number;
  previousRABillsValue: number;
  currentBillValue: number;
  balanceWorksForRABill: number;
  paymentStatus: 'Unpaid' | 'Partially Paid' | 'Paid';
  
  // Audit fields
  createdBy: string;
  createdOn: string;
  submittedBy?: string;
  submittedOn?: string;
  lastActionBy?: string;
  lastActionDate?: string;
  approvedBy?: string;
  approvedOn?: string;
  postedBy?: string;
  postedOn?: string;
  slaAge: number; // Days since submission
  
  // Payment tracking
  paidAmount: number;
  balanceAmount: number;
  lastPaymentDate?: string;
  paymentMode?: string;
  paymentRefNo?: string;
  
  // Documents and notes
  notes?: string;
  attachments: Array<{
    id: string;
    fileName: string;
    fileType: string;
    fileUrl: string;
  }>;
  comments?: Array<{
    id: string;
    userId: string;
    userName: string;
    comment: string;
    timestamp: string;
    stage: string;
  }>;
  
  // Flags
  softHold: boolean;
  hardHold: boolean;
  payOnCompletion: boolean;
  completionCertificate: boolean;
  
  // Overdue tracking
  dueDate?: string;
  overdueBy?: number; // Days
}

export interface RABillLine {
  id: string;
  raBillId: string;
  workLineId: string;
  workLineNumber: string;
  workLineTitle: string;
  description: string;
  uom: string;
  contractQuantity: number;
  cumulativeApprovedQuantity: number;
  previousBilledQuantity: number;
  availableQuantity: number; // Cum Approved - Prev Billed
  thisBillQuantity: number; // User input
  rate: number;
  lineSubtotal: number; // Qty × Rate
  gstPercentage: number;
  gstValue: number; // Subtotal × GST %
  lineTotal: number; // Subtotal + GST Value
}

export interface RABillDeduction {
  id: string;
  raBillId: string;
  type: 'Mobilization' | 'Retention' | 'On-Hold' | 'TDS';
  amount: number;
  percentage?: number;
  remarks?: string;
  maxAmount?: number;
  isManual?: boolean;
  taxableBase?: string;
  calculationType?: 'Percentage' | 'Amount';
  tdsSection?: string;
  sectionDescription?: string;
  thresholdAmount?: number;
}

export interface RABillTotals {
  raBillId: string;
  subtotalExclTax: number;
  gstTotal: number;
  grossTotal: number;
  mobilizationRecovery: number;
  retention: number;
  onHold: number;
  tds: number;
  totalDeductions: number;
  roundOff: number;
  netPayable: number;
}

export interface PaymentTransaction {
  id: string;
  raBillId: string;
  paymentDate: string;
  amount: number;
  paymentMode: string;
  referenceNumber: string;
  remarks?: string;
}

// Mock Data
export const mockRABills: RABill[] = [
  {
    id: "1",
    raBillNumber: "RA/2025/URB/0007",
    workOrderId: "wo1",
    workOrderNumber: "WO/2025/URB/0012",
    contractor: "ABC Infra Ltd",
    contractorId: "c1",
    status: 'Submitted',
    stage: 'Submitted',
    raPeriod: {
      from: "2025-07-01",
      to: "2025-07-31"
    },
    site: "Urban Rise - Tower A",
    project: "Tower Construction Phase 2",
    section: "Basement & Ground Floor",
    placeOfSupply: "Maharashtra",
    contractorGSTIN: "27ABCDE1234F1Z5",
    companyGSTIN: "27XYZTL9876M1Z8",
    taxRegime: 'CGST/SGST',
    tdsSection: "194C",
    tdsRate: 1.0,
    
    // Commercial Summary
    woTotalValue: 5000000,
    cumulativeWorkDone: 70.00,
    cumulativeWorkValue: 3500000,
    previousRABillsValue: 2500000,
    currentBillValue: 1000000,
    balanceWorksForRABill: 1500000,
    paymentStatus: 'Unpaid',
    
    // Audit
    createdBy: "contractor1",
    createdOn: "2025-08-01T10:00:00Z",
    submittedBy: "contractor1",
    submittedOn: "2025-08-05T14:30:00Z",
    slaAge: 3,
    
    // Payment
    paidAmount: 0,
    balanceAmount: 1000000,
    
    // Documents
    notes: "All measurement sheets and photos attached",
    attachments: [
      {
        id: "att1",
        fileName: "Measurement_Sheet_July.pdf",
        fileType: "pdf",
        fileUrl: "/documents/measurement_july.pdf"
      },
      {
        id: "att2",
        fileName: "Site_Photos.zip",
        fileType: "zip",
        fileUrl: "/documents/site_photos.zip"
      }
    ],
    comments: [],
    
    // Flags
    softHold: false,
    hardHold: false,
    payOnCompletion: false,
    completionCertificate: true
  },
  {
    id: "2",
    raBillNumber: "RA/2025/URB/0008",
    workOrderId: "wo2",
    workOrderNumber: "WO/2025/URB/0015",
    contractor: "XYZ Constructions",
    contractorId: "c2",
    status: 'Approved',
    stage: 'Approved',
    raPeriod: {
      from: "2025-07-01",
      to: "2025-07-31"
    },
    site: "Urban Rise - Tower B",
    project: "Tower Construction Phase 2",
    section: "Foundation & Basement",
    placeOfSupply: "Maharashtra",
    contractorGSTIN: "27XYZAB5678G2Z9",
    companyGSTIN: "27XYZTL9876M1Z8",
    taxRegime: 'CGST/SGST',
    tdsSection: "194C",
    tdsRate: 1.0,
    
    // Commercial Summary
    woTotalValue: 4200000,
    cumulativeWorkDone: 60.71,
    cumulativeWorkValue: 2550000,
    previousRABillsValue: 1700000,
    currentBillValue: 850000,
    balanceWorksForRABill: 1650000,
    paymentStatus: 'Partially Paid',
    
    // Audit
    createdBy: "contractor2",
    createdOn: "2025-07-28T09:15:00Z",
    submittedBy: "contractor2",
    submittedOn: "2025-08-02T16:45:00Z",
    approvedBy: "approver1",
    approvedOn: "2025-08-06T11:20:00Z",
    slaAge: 0,
    
    // Payment
    paidAmount: 400000,
    balanceAmount: 450000,
    lastPaymentDate: "2025-08-07",
    paymentMode: "Bank Transfer",
    paymentRefNo: "NEFT12345",
    
    // Documents
    notes: "Quality certification provided",
    attachments: [
      {
        id: "att3",
        fileName: "Quality_Certificate.pdf",
        fileType: "pdf",
        fileUrl: "/documents/quality_cert.pdf"
      }
    ],
    comments: [
      {
        id: "c1",
        userId: "checker1",
        userName: "John Checker",
        comment: "All measurements verified successfully",
        timestamp: "2025-08-03T10:30:00Z",
        stage: "Checked"
      }
    ],
    
    // Flags
    softHold: false,
    hardHold: false,
    payOnCompletion: false,
    completionCertificate: true
  },
  {
    id: "3",
    raBillNumber: "RA/2025/URB/0009",
    workOrderId: "wo3",
    workOrderNumber: "WO/2025/URB/0018",
    contractor: "BuildTech Pvt Ltd",
    contractorId: "c3",
    status: 'Paid',
    stage: 'Paid',
    raPeriod: {
      from: "2025-06-01",
      to: "2025-06-30"
    },
    site: "Urban Rise - Tower C",
    project: "Tower Construction Phase 1",
    section: "Superstructure",
    placeOfSupply: "Maharashtra",
    contractorGSTIN: "27BUILDTECH123Z4",
    companyGSTIN: "27XYZTL9876M1Z8",
    taxRegime: 'CGST/SGST',
    tdsSection: "194C",
    tdsRate: 1.0,
    
    // Commercial Summary
    woTotalValue: 6000000,
    cumulativeWorkDone: 45.00,
    cumulativeWorkValue: 2700000,
    previousRABillsValue: 1500000,
    currentBillValue: 1200000,
    balanceWorksForRABill: 3300000,
    paymentStatus: 'Paid',
    
    // Audit
    createdBy: "contractor3",
    createdOn: "2025-06-28T08:00:00Z",
    submittedBy: "contractor3",
    submittedOn: "2025-07-01T12:00:00Z",
    approvedBy: "approver1",
    approvedOn: "2025-07-15T14:30:00Z",
    postedBy: "finance1",
    postedOn: "2025-07-16T09:00:00Z",
    slaAge: 0,
    
    // Payment
    paidAmount: 1200000,
    balanceAmount: 0,
    lastPaymentDate: "2025-08-01",
    paymentMode: "Bank Transfer",
    paymentRefNo: "RTGS98765",
    
    // Documents
    notes: "Completed work as per schedule",
    attachments: [],
    comments: [],
    
    // Flags
    softHold: false,
    hardHold: false,
    payOnCompletion: false,
    completionCertificate: true,
    
    // Overdue (not applicable as paid)
    dueDate: "2025-07-31",
    overdueBy: 0
  }
];

export const mockRABillLines: RABillLine[] = [
  // RA Bill 1 Lines
  {
    id: "line1",
    raBillId: "1",
    workLineId: "wl1",
    workLineNumber: "WOL-001",
    workLineTitle: "Foundation Digging",
    description: "Basement Zone A trenching",
    uom: "Cum",
    contractQuantity: 500,
    cumulativeApprovedQuantity: 260,
    previousBilledQuantity: 180,
    availableQuantity: 80,
    thisBillQuantity: 80,
    rate: 1200,
    lineSubtotal: 96000,
    gstPercentage: 18,
    gstValue: 17280,
    lineTotal: 113280
  },
  {
    id: "line2",
    raBillId: "1",
    workLineId: "wl2",
    workLineNumber: "WOL-002",
    workLineTitle: "PCC Laying",
    description: "M10 PCC in basement",
    uom: "Cum",
    contractQuantity: 300,
    cumulativeApprovedQuantity: 160,
    previousBilledQuantity: 100,
    availableQuantity: 60,
    thisBillQuantity: 60,
    rate: 3000,
    lineSubtotal: 180000,
    gstPercentage: 18,
    gstValue: 32400,
    lineTotal: 212400
  },
  // RA Bill 2 Lines
  {
    id: "line3",
    raBillId: "2",
    workLineId: "wl3",
    workLineNumber: "WOL-003",
    workLineTitle: "Steel Reinforcement",
    description: "TMT bars for foundation",
    uom: "Kg",
    contractQuantity: 2000,
    cumulativeApprovedQuantity: 1200,
    previousBilledQuantity: 800,
    availableQuantity: 400,
    thisBillQuantity: 300,
    rate: 85,
    lineSubtotal: 25500,
    gstPercentage: 18,
    gstValue: 4590,
    lineTotal: 30090
  }
];

export const mockRABillDeductions: RABillDeduction[] = [
  {
    id: "ded1",
    raBillId: "1",
    type: "Mobilization",
    amount: 50000
  },
  {
    id: "ded2",
    raBillId: "1",
    type: "Retention",
    amount: 13800,
    percentage: 5.0
  },
  {
    id: "ded3",
    raBillId: "1",
    type: "TDS",
    amount: 2760,
    percentage: 1.0
  }
];

export const mockRABillTotals: RABillTotals[] = [
  {
    raBillId: "1",
    subtotalExclTax: 276000,
    gstTotal: 49680,
    grossTotal: 325680,
    mobilizationRecovery: 50000,
    retention: 13800,
    onHold: 0,
    tds: 2760,
    totalDeductions: 66560,
    roundOff: 0,
    netPayable: 259120
  }
];

export const mockPaymentTransactions: PaymentTransaction[] = [
  {
    id: "pay1",
    raBillId: "2",
    paymentDate: "2025-08-07",
    amount: 400000,
    paymentMode: "Bank Transfer",
    referenceNumber: "NEFT12345",
    remarks: "Partial payment"
  },
  {
    id: "pay2",
    raBillId: "3",
    paymentDate: "2025-08-01",
    amount: 1200000,
    paymentMode: "RTGS",
    referenceNumber: "RTGS98765",
    remarks: "Full payment"
  }
];